import React, { MouseEvent, ReactNode, useCallback, useMemo } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { faRoute, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  alpha,
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Image,
  lighten,
  Skeleton,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";

const ImageIcon = ({ src }: { src: string }) =>
  src ? (
    <Image
      radius="xs"
      width={25}
      fit="contain"
      height={30}
      src={src}
      // Mantine breaks the UI here by adding a class with
      // width: 100% to the image, so we need to override it
      style={{ width: "auto" }}
    />
  ) : (
    <Skeleton width={20} height={20} circle />
  );

export type ItemProps = {
  id: number;
  index: number;
  name: string;
  onClick?: (e: MouseEvent, index: number) => void;
  onRightButtonClick: (id: number) => void;
  selected: boolean;
  hidden?: boolean;
  bottomInfo?: string;
  leftImageSrc?: string;
  leftIcon?: ReactNode;
  isSelectMode?: boolean;
  isMultiselected?: boolean;
  trackIconColor?: string;
};

export const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  (
    {
      id,
      index,
      name,
      onClick,
      onRightButtonClick,
      selected,
      hidden = false,
      bottomInfo,
      leftIcon,
      leftImageSrc,
      trackIconColor,
      isSelectMode,
      isMultiselected: isSelectModeSelected,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useShownColorScheme();
    const handleRightButtonClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onRightButtonClick(id);
      },
      [id, onRightButtonClick]
    );

    const handleClick = useCallback(
      (e: MouseEvent) => onClick?.(e, index),
      [index, onClick]
    );

    const LeftSideIcon = useMemo(
      () => (
        <Box sx={{ userSelect: "none", opacity: hidden ? 0.7 : undefined }}>
          {leftIcon && leftIcon}
          {leftImageSrc && <ImageIcon src={leftImageSrc} />}
          {trackIconColor && (
            <FontAwesomeIcon
              icon={faRoute}
              color={trackIconColor}
              fontSize={20}
              // opacity={hidden ? 0.7 : undefined}
            />
          )}
        </Box>
      ),
      [hidden, leftIcon, leftImageSrc, trackIconColor]
    );

    return useMemo(
      () => (
        <Group
          ref={ref}
          onClick={handleClick}
          gap="sm"
          px={13}
          py={8}
          key={id}
          sx={(theme, u) => ({
            [u.dark]: {
              backgroundColor: selected
                ? alpha(theme.colors.violet[9], 0.5)
                : "transparent",
            },
            [u.light]: {
              backgroundColor: selected
                ? alpha(theme.colors.violet[5], 0.3)
                : "transparent",
            },
            position: "relative",
            flexWrap: "nowrap",
            cursor: "pointer",
            boxShadow: selected ? "inset 0 0 5px #00000017" : "none",
            "&:hover": {
              [u.dark]: {
                backgroundColor: selected
                  ? alpha(lighten(theme.colors.violet[7], 0.2), 0.4)
                  : alpha("#fff", 0.08),
              },
              [u.light]: {
                backgroundColor: selected
                  ? alpha(lighten(theme.colors.violet[5], 0.3), 0.3)
                  : alpha("#000", 0.08),
              },
            },
            userSelect: isSelectMode ? "none" : "initial",
          })}
        >
          {LeftSideIcon}
          <Stack sx={{ flexGrow: 1 }} gap={3}>
            <Text
              sx={(theme, u) => ({
                [u.dark]: {
                  color: theme.colors.gray[3],
                },
                [u.light]: {
                  color: "#000",
                },
                fontSize: 14,
                flexGrow: 1,
                textOverflow: "ellipsis",
                overflowX: "hidden",
                overflowWrap: "anywhere",
                lineHeight: 1.2,
                opacity: hidden ? 0.7 : undefined,
              })}
              lineClamp={2}
            >
              {name}
            </Text>
            {bottomInfo && (
              <Text
                size="xs"
                sx={(theme, u) => ({
                  [u.dark]: {
                    color: theme.colors.dark[1],
                  },
                  [u.light]: {
                    color: selected
                      ? theme.colors.dark[3]
                      : theme.colors.dark[2],
                  },
                  userSelect: isSelectMode ? "none" : "initial",
                  opacity: hidden ? 0.7 : undefined,
                })}
              >
                {bottomInfo}
              </Text>
            )}
          </Stack>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexGrow: 1,
              width: 28,
              height: 28,
            }}
          >
            {isSelectMode ? (
              <div style={{ padding: "5px" }}>
                <Checkbox
                  checked={isSelectModeSelected}
                  onChange={undefined}
                  readOnly
                />
              </div>
            ) : (
              <ActionIcon
                onClick={handleRightButtonClick}
                color={
                  hidden
                    ? "gray"
                    : colorScheme === "dark"
                      ? theme.colors.violet[2]
                      : theme.colors.violet[7]
                }
                variant="transparent"
              >
                <FontAwesomeIcon
                  opacity={hidden ? 0.7 : undefined}
                  icon={hidden ? faEyeSlash : faEye}
                />
              </ActionIcon>
            )}
          </div>
        </Group>
      ),
      [
        colorScheme,
        ref,
        handleClick,
        id,
        selected,
        hidden,
        isSelectMode,
        LeftSideIcon,
        name,
        bottomInfo,
        isSelectModeSelected,
        handleRightButtonClick,
        theme.colors.violet,
      ]
    );
  }
);
Item.displayName = "Item";
